import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Signer } from '@aws-amplify/core/internals/utils';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthSession } from '../model/auth.model';
import { AuthStateService } from '../core/auth/auth-state.service';
import { AuthService } from '../core/auth/auth.service';

// Requests are made against '/api/...' and resolved to environment.apiUrl here
// (or forwarded by the dev-server proxy when environment.useProxy is set)
const API_URL_EXCLUSIONS = ['/assets', 'assets'];

const HEADER_NAMES = {
  authorization: 'Authorization',
  cognitoToken: 'CognitoToken',
  xAmzSecurityToken: 'X-Amz-Security-Token',
  xAmzDate: 'x-amz-date',
  xHostOverride: 'X-Host-Override',
};

const EXPIRED_TOKEN_MESSAGES = ['The security token included in the request is expired', 'token is expired'];

type HeadersType = Record<string, string | string[]>;

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly authState = inject(AuthStateService);
  private readonly authService = inject(AuthService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const session = this.authState.session();

    if (!this.authState.isAuthenticated() || !session) {
      this.authService.federatedSignIn();
      return throwError(() => new Error('not authenticated'));
    }

    const signedReq = this.signAndCombineRequest(session, req.clone({ setHeaders: this.createStaticHeaders(session) }));

    return next.handle(signedReq).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse) {
          if (error.status === HttpStatusCode.Unauthorized || (error.error?.message && EXPIRED_TOKEN_MESSAGES.some((m) => error.error.message.includes(m)))) {
            this.authService.federatedSignIn();
          }
        }
        return throwError(() => error);
      }),
    );
  }

  private signAndCombineRequest(session: AuthSession, req: HttpRequest<unknown>): HttpRequest<unknown> {
    const rawRequest = this.createRawRequest(req);

    const accessInfo = {
      access_key: session.credentials.accessKeyId,
      secret_key: session.credentials.secretAccessKey,
      session_token: session.credentials.sessionToken,
    };

    const serviceInfo = {
      region: environment.awsCognitoConfig.region,
      service: 'execute-api',
    };

    const signedRaw = Signer.sign(rawRequest, accessInfo, serviceInfo);

    const headers: HeadersType = {
      [HEADER_NAMES.authorization]: signedRaw.headers[HEADER_NAMES.authorization],
      [HEADER_NAMES.cognitoToken]: signedRaw.headers[HEADER_NAMES.cognitoToken],
      [HEADER_NAMES.xAmzSecurityToken]: signedRaw.headers[HEADER_NAMES.xAmzSecurityToken],
      [HEADER_NAMES.xAmzDate]: signedRaw.headers[HEADER_NAMES.xAmzDate],
    };

    if (environment.awsCognitoConfig.headerXHostOverride) {
      headers[HEADER_NAMES.xHostOverride] = signedRaw.headers['host'];
    }

    return req.clone({
      url: environment.useProxy ? req.url : this.resolveFullApiUrl(req.url),
      setHeaders: headers,
    });
  }

  private createRawRequest(req: HttpRequest<unknown>) {
    const headers: HeadersType = {};
    req.headers.keys().forEach((key) => {
      const value = req.headers.get(key);
      if (value) headers[key] = value;
    });

    return {
      method: req.method,
      url: this.resolveFullApiUrl(req.urlWithParams),
      headers,
      data: req.serializeBody(),
    };
  }

  private createStaticHeaders(session: AuthSession): HeadersType {
    return { [HEADER_NAMES.cognitoToken]: session.user.idToken.jwtToken };
  }

  private resolveFullApiUrl(uri: string): string {
    const isApiUrl = !API_URL_EXCLUSIONS.find((p) => uri.indexOf(p, 0) === 0);
    if (isApiUrl) {
      uri = uri.replace('/api/', `${environment.apiUrl}/`);
    }
    return uri;
  }
}
